import type { MistakeDto } from "@eb/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GraduationCap, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorBanner, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime, formatPercent } from "../lib/format";

/**
 * The mistake collection: every question the learner has answered wrongly, in any mode,
 * until they remove it here. Entries come back automatically on the next wrong answer.
 */
export function MistakesPage() {
    const queryClient = useQueryClient();
    const mistakesQuery = useQuery({ queryKey: ["mistakes"], queryFn: api.mistakes.list });
    const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
    const showTranslation = settingsQuery.data?.settings.showQuestionTranslation ?? false;

    // Removal asks once, inline, instead of a confirm() dialog: the entry can be
    // re-added by simply getting the question wrong again, so the stakes are low.
    const [confirming, setConfirming] = useState<number | null>(null);

    const removeMutation = useMutation({
        mutationFn: (number: number) => api.mistakes.remove(number),
        onSuccess: (_, number) => {
            queryClient.setQueryData<{ items: MistakeDto[] }>(["mistakes"], (previous) =>
                previous ? { items: previous.items.filter((item) => item.question.number !== number) } : previous
            );
            // The study filter draws from the same table.
            void queryClient.invalidateQueries({ queryKey: ["queue", "MISTAKES"] });
            setConfirming(null);
        }
    });

    if (mistakesQuery.isLoading) return <Spinner label="正在加载错题集" />;
    if (mistakesQuery.isError) {
        return <ErrorBanner message="加载错题集失败" onRetry={() => void mistakesQuery.refetch()} />;
    }

    const items = mistakesQuery.data?.items ?? [];

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-ink">错题集</h1>
                    <p className="mt-1 text-sm text-ink-soft">
                        闪卡、练题和模拟考里答错的题都会自动收进来，直到你把它移除。再答错会重新加入。
                    </p>
                </div>
                {items.length > 0 ? (
                    <Link to="/study?filter=MISTAKES">
                        <Button>
                            <GraduationCap className="size-4" aria-hidden />
                            练习错题（{items.length}）
                        </Button>
                    </Link>
                ) : null}
            </div>

            {removeMutation.isError ? <ErrorBanner message="移除失败，请重试" /> : null}

            {items.length === 0 ? (
                <EmptyState
                    title="错题集是空的"
                    description="目前没有答错过的题，或者你已经把它们都移除了。去学习或模考一轮，错题会自动出现在这里。"
                    action={
                        <Link to="/study">
                            <Button variant="secondary">去学习</Button>
                        </Link>
                    }
                />
            ) : (
                <ul className="space-y-2">
                    {items.map((item) => {
                        const { question, card } = item;
                        const isConfirming = confirming === question.number;
                        return (
                            <li key={question.number}>
                                <Card className="flex items-start gap-3 px-4 py-3">
                                    <span className="mt-0.5 w-10 shrink-0 text-sm text-ink-soft tabular-nums">
                                        #{question.number}
                                    </span>

                                    <div className="min-w-0 flex-1 space-y-1.5">
                                        <Link to={`/questions/${question.number}`} className="block hover:underline">
                                            <p lang="de" className="line-clamp-2 text-sm text-ink">
                                                {question.text}
                                            </p>
                                            {showTranslation && question.textZh ? (
                                                <p lang="zh-Hans" className="line-clamp-2 text-xs text-ink-soft">
                                                    {question.textZh}
                                                </p>
                                            ) : null}
                                        </Link>
                                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-soft">
                                            <Badge tone="bad">错 {item.wrongCount} 次</Badge>
                                            {question.scope === "STATE" ? (
                                                <Badge tone="brand">{question.state}</Badge>
                                            ) : null}
                                            <span>最近答错 {formatDateTime(item.lastWrongAt)}</span>
                                            {card && card.answerCount > 0 ? (
                                                <span>· 总正确率 {formatPercent(card.correctCount / card.answerCount)}</span>
                                            ) : null}
                                        </div>
                                    </div>

                                    {isConfirming ? (
                                        <div className="flex shrink-0 items-center gap-1.5">
                                            <Button
                                                variant="danger"
                                                loading={removeMutation.isPending}
                                                onClick={() => removeMutation.mutate(question.number)}
                                                className="px-3 py-1.5"
                                            >
                                                确认移除
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => setConfirming(null)}
                                                className="px-3 py-1.5"
                                            >
                                                取消
                                            </Button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setConfirming(question.number)}
                                            aria-label={`把第 ${question.number} 题移出错题集`}
                                            className="shrink-0 rounded-lg p-1.5 text-ink-soft transition-colors hover:bg-bad-soft hover:text-bad"
                                        >
                                            <Trash2 className="size-4" aria-hidden />
                                        </button>
                                    )}
                                </Card>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
